import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { money } from "@/lib/api";
import { DateRange, presetRange, toApiRange } from "@/components/Filters";
import { Users, CalendarCheck, UserCheck, UserX, GraduationCap, IndianRupee } from "lucide-react";

const CELL = [
  ["leads_in", "Leads in", Users, "text-blue-600"],
  ["demos_booked", "Demos booked", CalendarCheck, "text-amber-600"],
  ["demos_attended", "Demos attended", UserCheck, "text-emerald-600"],
  ["no_shows", "No-shows", UserX, "text-red-600"],
  ["admissions", "Admissions", GraduationCap, "text-violet-600"],
];

export default function PeriodStats() {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const r = toApiRange(range);
  const { data } = useQuery({
    queryKey: ["period", r],
    queryFn: async () => (await api.get("/reports/range", { params: { start: r.date_from, end: r.date_to || new Date().toISOString() } })).data,
  });
  return (
    <div className="mt-6 rounded-xl border bg-white dark:bg-slate-800 p-5" data-testid="period-stats">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="font-head font-bold">Performance for a period</div>
        <DateRange preset={preset} setPreset={setPreset} range={range} setRange={setRange} testid="period-range" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CELL.map(([k, label, Icon, cls]) => (
          <div key={k} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border" data-testid={`period-${k}`}>
            <Icon className={`w-4 h-4 ${cls}`} />
            <div className="text-xl font-extrabold mt-1">{data?.[k] ?? "—"}</div>
            <div className="text-[11px] text-slate-500">{label}</div>
          </div>
        ))}
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border" data-testid="period-collections">
          <IndianRupee className="w-4 h-4 text-emerald-600" />
          <div className="text-xl font-extrabold mt-1">{data ? money(data.collections_verified) : "—"}</div>
          <div className="text-[11px] text-slate-500">Verified collections</div>
        </div>
      </div>
      {data?.counsellors?.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <div className="text-sm font-semibold mb-2 text-slate-600 dark:text-slate-300">By counsellor</div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Name</th><th className="text-left py-2">Leads</th><th className="text-left py-2">Attended</th><th className="text-left py-2">Admissions</th></tr></thead>
            <tbody>
              {data.counsellors.map((c) => (
                <tr key={c.name} className="border-t"><td className="py-2 font-medium">{c.name}</td><td className="py-2">{c.leads}</td><td className="py-2 text-emerald-600">{c.attended}</td><td className="py-2 text-violet-600">{c.admissions}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
